import React, { useState } from "react";
import { useSelector } from "react-redux";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const AGENT_SIGNUP_PATH = "/accounts?q=sign-up";
// const MERCHANT_SIGNUP_PATH = "/";

function CopyField({ label, value, icon }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={styles.copyRow}>
      <div style={styles.copyLabel}>
        <i className={icon} style={{ color: PRIMARY, marginRight: 6 }} />
        {label}
      </div>
      <div style={styles.copyBox}>
        <span style={styles.copyText}>{value}</span>
        <button
          style={{
            ...styles.copyBtn,
            ...(copied ? styles.copyBtnSuccess : {}),
          }}
          onClick={handleCopy}
        >
          {copied ? (
            <>
              <i className='bi bi-check2' /> Copied
            </>
          ) : (
            <>
              <i className='bi bi-clipboard' /> Copy
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function WhatsAppButton({ text }) {
  const encoded = encodeURIComponent(text);
  return (
    <a
      href={`https://wa.me/?text=${encoded}`}
      target='_blank'
      rel='noopener noreferrer'
      style={styles.waBtn}
    >
      <i className='bi bi-whatsapp' style={{ marginRight: 6 }} />
      Share via WhatsApp
    </a>
  );
}

export default function Referral() {
  const dashboard = useSelector((s) => s.profile.extAdminDashboard);
  const refCode = dashboard?.adminRefCode || "—";

  const origin = window.location.origin;
  const agentLink = `${origin}${AGENT_SIGNUP_PATH}&ref=${refCode}`;
  const merchantLink = `https://www.exciteenterprise.com/register?ref=${refCode}`;

  const agentMsg = `Join my team as a Sales Agent on Excite Enterprise!\nUse this link to sign up: ${agentLink}`;
  const merchantMsg = `Register your business on Excite Enterprise and get a free 30-day subscription!\nUse my referral link: ${merchantLink}`;

  return (
    <div style={styles.root}>
      <h2 style={styles.heading}>Referral Code</h2>
      <p style={styles.subText}>
        Share your referral code or links to onboard new agents and merchants.
        Agents assigned via your link are automatically placed under your
        management.
      </p>

      {/* Big Code Display */}
      <div style={styles.codeBanner}>
        <div style={styles.codeBannerLabel}>Your Referral Code</div>
        <div style={styles.codeDisplay}>{refCode}</div>
        <button
          style={styles.copyCodeBtn}
          onClick={() => navigator.clipboard.writeText(refCode)}
        >
          <i className='bi bi-clipboard' style={{ marginRight: 6 }} />
          Copy Code
        </button>
      </div>

      {/* Agent Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <i
            className='bi bi-person-badge-fill'
            style={{ color: PRIMARY, marginRight: 8, fontSize: 18 }}
          />
          <span style={styles.sectionTitle}>Sales Agent Signup Link</span>
        </div>
        <p style={styles.sectionDesc}>
          Send this link to someone you want to register as a Sales Agent under
          your management. Their account will be automatically linked to you.
        </p>
        <CopyField
          label='Agent Signup Link'
          value={agentLink}
          icon='bi bi-link-45deg'
        />
        <div style={{ marginTop: 12 }}>
          <WhatsAppButton text={agentMsg} />
        </div>
      </div>

      {/* Merchant Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <i
            className='bi bi-shop-window'
            style={{ color: PRIMARY, marginRight: 8, fontSize: 18 }}
          />
          <span style={styles.sectionTitle}>Merchant Signup Link</span>
        </div>
        <p style={styles.sectionDesc}>
          Share this link with businesses. Merchants who register through your
          link will be tracked under your referrals and you'll earn commission
          on their subscriptions.
        </p>
        <CopyField
          label='Merchant Signup Link'
          value={merchantLink}
          icon='bi bi-link-45deg'
        />
        <div style={{ marginTop: 12 }}>
          <WhatsAppButton text={merchantMsg} />
        </div>
      </div>

      {/* How it works */}
      <div style={styles.howCard}>
        <div style={styles.sectionTitle}>How it works</div>
        <ul style={styles.howList}>
          <li>
            <i
              className='bi bi-1-circle-fill'
              style={{ color: PRIMARY, marginRight: 8 }}
            />
            Share the agent link — they sign up and are automatically placed
            under your management in Sector 4.
          </li>
          <li>
            <i
              className='bi bi-2-circle-fill'
              style={{ color: PRIMARY, marginRight: 8 }}
            />
            Share the merchant link — their subscription activates and you earn
            a commission tracked on your dashboard.
          </li>
          <li>
            <i
              className='bi bi-3-circle-fill'
              style={{ color: PRIMARY, marginRight: 8 }}
            />
            You can also share just the code <strong>{refCode}</strong> — both
            agents and merchants can enter it manually during signup.
          </li>
        </ul>
      </div>
    </div>
  );
}

const styles = {
  root: { paddingBottom: 40 },
  heading: { fontSize: 22, fontWeight: 700, color: BG, marginBottom: 6 },
  subText: { color: "#888", fontSize: 14, marginBottom: 28, maxWidth: 640 },
  codeBanner: {
    background: BG,
    borderRadius: 12,
    padding: "28px 32px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 32,
    textAlign: "center",
  },
  codeBannerLabel: {
    color: "rgba(255,255,255,0.6)",
    fontSize: 12,
    letterSpacing: 2,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  codeDisplay: {
    fontSize: 42,
    fontWeight: 800,
    letterSpacing: 8,
    color: PRIMARY,
    fontFamily: "monospace",
    marginBottom: 18,
  },
  copyCodeBtn: {
    background: "transparent",
    border: `1.5px solid ${PRIMARY}`,
    color: PRIMARY,
    borderRadius: 6,
    padding: "8px 20px",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
  },
  section: {
    background: "#fff",
    borderRadius: 10,
    padding: "22px 24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    marginBottom: 20,
  },
  sectionHeader: { display: "flex", alignItems: "center", marginBottom: 6 },
  sectionTitle: { fontSize: 15, fontWeight: 700, color: BG },
  sectionDesc: { color: "#888", fontSize: 13, marginBottom: 16, maxWidth: 560 },
  copyRow: { display: "flex", flexDirection: "column", gap: 6 },
  copyLabel: { fontSize: 12, color: "#666", fontWeight: 600 },
  copyBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    overflow: "hidden",
  },
  copyText: {
    flex: 1,
    padding: "10px 14px",
    fontSize: 13,
    color: "#333",
    background: "#fafafa",
    fontFamily: "monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  copyBtn: {
    background: BG,
    color: "#fff",
    border: "none",
    padding: "10px 16px",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexShrink: 0,
  },
  copyBtnSuccess: { background: "#4caf50" },
  waBtn: {
    display: "inline-flex",
    alignItems: "center",
    background: "#25D366",
    color: "#fff",
    borderRadius: 6,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  },
  howCard: {
    background: "rgba(167,204,72,0.07)",
    border: `1.5px solid rgba(167,204,72,0.3)`,
    borderRadius: 10,
    padding: "20px 24px",
  },
  howList: {
    listStyle: "none",
    padding: 0,
    margin: "12px 0 0 0",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
};
